import { motion } from "framer-motion";
import { PageHeader } from "../components/nk/PageHeader";
import { CtaBand } from "../components/nk/CtaBand";
import { Seo } from "../components/nk/Seo";
import { ALTERATIONS, ALTERATION_STEPS } from "../data";

const ease = [0.22, 1, 0.36, 1];
const IMG = {
  fitting: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/06jn3m8s_hf_20260829_073012_4a349be9-8946-4eba-bf6b-fb32925068d1.png",
  hem: "https://customer-assets-jai6qajn.emergentagent.net/job_bridal-australia-hub/artifacts/amolnixo_hf_20260829_072401_dc04abf0-e0fd-4e40-af6e-824e7a64c153.png",
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function AlterationsPage() {
  return (
    <>
      <Seo
        title="Wedding Dress Alterations in Perth — Expert Bridal Tailoring | NK Bridal"
        description="Expert wedding dress alterations in Perth by Nancy, a renowned Brisbane seamstress — for gowns bought anywhere. Hand-pinned, sewn and finished for a flawless fit."
        path="/alterations"
      />
      <PageHeader
        eyebrow="Service"
        title={["Wedding", "alterations"]}
        intro="Expert bridal alterations by Nancy in Perth — for gowns bought anywhere. Every adjustment is pinned, sewn and finished by hand for a fit that feels effortless."
      />

      {/* Intro split */}
      <section className="pb-24 md:pb-40" data-testid="alterations-intro">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <Reveal className="md:col-span-6 relative overflow-hidden aspect-[4/5] border border-line bg-stone">
            <img src={IMG.fitting} alt="Nancy measuring a bride for alterations" className="w-full h-full object-cover object-center" />
          </Reveal>
          <div className="md:col-span-5 md:col-start-8">
            <Reveal>
              <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight mb-6">
                A flawless fit, <span className="italic">by hand</span>
              </h2>
              <p className="text-ink/70 font-light leading-relaxed mb-4">
                Whether your gown came from NK Bridal or elsewhere, Nancy will tailor it to your body with the same couture care she brings to her own designs.
              </p>
              <p className="text-ink/70 font-light leading-relaxed">
                Bridal, bridesmaid and formal wear all welcome. Alterations are quoted individually at your first fitting — honest pricing, no surprises.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-40 bg-stone" data-testid="alterations-services">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-champagne mb-5">What we alter</p>
            <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight mb-16 md:mb-20">
              Every detail, considered
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
            {ALTERATIONS.map((a, i) => (
              <Reveal key={a.t} delay={(i % 2) * 0.06}>
                <div className="border-t border-line pt-5 flex gap-6">
                  <span className="font-serif text-champagne/70 text-2xl leading-none">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl font-light mb-2">{a.t}</h3>
                    <p className="text-ink/60 font-light leading-relaxed">{a.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-14 md:mt-20 font-serif italic text-2xl md:text-3xl text-ink/80 max-w-3xl">
              Steaming and a final press are included with every alteration.
            </p>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-40" data-testid="alterations-steps">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16">
          <Reveal className="md:col-span-5 relative overflow-hidden aspect-[4/5] border border-line bg-stone md:sticky md:top-32 h-fit">
            <img src={IMG.hem} alt="Nancy steaming and pressing a wedding gown" className="w-full h-full object-cover object-center" />
          </Reveal>
          <div className="md:col-span-6 md:col-start-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.35em] text-champagne mb-5">How it works</p>
              <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight mb-12 md:mb-16">
                Four simple steps
              </h2>
            </Reveal>
            <div className="space-y-8">
              {ALTERATION_STEPS.map((s, i) => (
                <Reveal key={s.no} delay={i * 0.06}>
                  <div className="border-t border-ink pt-5 flex gap-6">
                    <span className="font-serif text-4xl md:text-5xl text-champagne/60 leading-none shrink-0">{s.no}</span>
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl font-light mb-2">{s.t}</h3>
                      <p className="text-ink/60 font-light leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
